import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Avatar } from "./badges";
import { Badge } from "./ui/badge";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";
import ChatWidget from "./ChatWidget";
import { FlaskConical, LayoutDashboard, FolderKanban, Users, Moon, Sun, LogOut, ClipboardList } from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") !== "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "dashboard-nav-link" },
    { to: "/projects", label: "Projects", icon: FolderKanban, testid: "projects-nav-link" },
    { to: "/my-work", label: "My Work", icon: ClipboardList, testid: "my-work-nav-link" },
    { to: "/team", label: "Team", icon: Users, testid: "team-nav-link" },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <FlaskConical className="h-5 w-5 text-primary" />
            <span className="font-heading font-bold tracking-tight hidden sm:inline">Nexus R&D</span>
          </Link>
          <nav className="flex items-center gap-1">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive(n.to) ? "bg-accent text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                }`}>
                <n.icon className="h-4 w-4" />
                <span className="hidden md:inline">{n.label}</span>
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <GlobalSearch />
            <NotificationBell />
            <Button variant="ghost" size="icon" data-testid="theme-toggle-btn" onClick={() => setDark(!dark)}>
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="flex items-center gap-2 pl-2 border-l border-border">
              <Avatar name={user?.name} size="md" />
              <div className="hidden sm:block leading-tight">
                <p className="text-sm font-medium" data-testid="current-user-name">{user?.name}</p>
                <Badge variant="outline" className="text-[9px] font-mono uppercase" data-testid="current-user-role">{user?.role}</Badge>
              </div>
              <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>
      <ChatWidget />
    </div>
  );
}
